import { useState } from 'react';
import {
  CloseCircle,
  DocumentText,
  Refresh,
  RotateRight,
  TickCircle,
  Trash,
  Warning2,
} from 'iconsax-react';

import {
  Filter,
  FilterClearBtn,
  FilterLabel,
  FilterOptionItem,
  FilterOptionsList,
  FilterTrigger,
} from '@design-system/filter-new';
import { cn } from '@design-system/helpers';
import ConfirmationDeleteDialog from '@shared/components/reusables/confrimation-delete-dialog';
import { OmniFilterTrigger } from '@features/conversations/components/shell/omni-list-toolbar';

import { useKnowledgeBaseFilters } from '../hooks/use-knowledge-base-filters';
import {
  knowledgeDocumentsApi,
  type KnowledgeDocument,
  type KnowledgeDocumentStatus,
} from '../services/knowledge-content-api';
import { formatKnowledgeDate } from '../utils/format-date';
import { AddFilesDialog } from './add-files-dialog';
import { ContentCard } from './content-card';
import { ContentList } from './content-list';
import { ContentToolbar } from './content-toolbar';

const STATUS_OPTIONS: { value: KnowledgeDocumentStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'PROCESSING', label: 'Indexing' },
  { value: 'READY', label: 'Ready' },
  { value: 'PARTIAL', label: 'Partially indexed' },
  { value: 'FAILED', label: 'Failed' },
];

/** The small icon beside a file name: where its indexing is. */
export function DocumentStatusIcon({
  status,
}: {
  status: KnowledgeDocumentStatus;
}) {
  const label =
    STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
  const common = {
    size: 18,
    variant: 'Bold' as const,
    'aria-hidden': true as const,
  };
  return (
    <span
      data-slot='document-status'
      data-status={status}
      role='img'
      aria-label={label}
      title={label}
      className={cn(
        'flex shrink-0 items-center',
        status === 'READY' && 'text-gray-400',
        (status === 'PENDING' || status === 'PROCESSING') &&
          'text-secondary-1000',
        status === 'PARTIAL' && 'text-amber-500',
        status === 'FAILED' && 'text-red-500'
      )}
    >
      {status === 'READY' ? (
        <TickCircle {...common} />
      ) : status === 'FAILED' ? (
        <CloseCircle {...common} />
      ) : status === 'PARTIAL' ? (
        <Warning2 {...common} />
      ) : (
        <RotateRight
          {...common}
          variant='Bold'
        />
      )}
    </span>
  );
}

export function DocumentsTab({
  kbaseId,
  softActions = false,
}: {
  kbaseId: number;
  /** Soft lavender add button (Tia panel). */
  softActions?: boolean;
}) {
  const { search } = useKnowledgeBaseFilters();
  const [statuses, setStatuses] = useState<KnowledgeDocumentStatus[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<KnowledgeDocument | null>(null);

  const list = knowledgeDocumentsApi.useFetchInfinite({
    kbase: kbaseId,
    ...(search && { q: search }),
    ...(statuses.length > 0 && { status: statuses }),
    page_size: 25,
  });
  const remove = knowledgeDocumentsApi.useDelete();
  const reindex = knowledgeDocumentsApi.useReindex();

  return (
    <>
      <ContentToolbar
        placeholder={softActions ? 'Search in Files' : 'Search in your files'}
        addLabel='Add File'
        addVariant={softActions ? 'primaryPale' : 'primary'}
        onAdd={() => setAdding(true)}
      >
        <Filter
          open={filterOpen}
          onOpenChange={setFilterOpen}
        >
          <FilterTrigger
            asChild
            aria-label='Filter files'
          >
            <OmniFilterTrigger
              open={filterOpen}
              className='h-10'
            />
          </FilterTrigger>
          <FilterOptionsList align='end'>
            <div className='flex items-center justify-between px-2 pt-1'>
              <FilterLabel>Status</FilterLabel>
              <FilterClearBtn onClearClick={() => setStatuses([])} />
            </div>
            {STATUS_OPTIONS.map((option) => (
              <FilterOptionItem
                key={option.value}
                selected={statuses.includes(option.value)}
                onClick={() =>
                  setStatuses((current) =>
                    current.includes(option.value)
                      ? current.filter((value) => value !== option.value)
                      : [...current, option.value]
                  )
                }
              >
                {option.label}
              </FilterOptionItem>
            ))}
          </FilterOptionsList>
        </Filter>
      </ContentToolbar>

      <ContentList
        query={list}
        searching={Boolean(search) || statuses.length > 0}
        emptyIcon={<DocumentText size={24} />}
        emptyTitle='No files yet'
        emptyDescription='Upload PDF, DOCX, Markdown, text or HTML files and the agent answers from them once indexed.'
        renderItem={(document) => {
          const indexing =
            document.status === 'PENDING' || document.status === 'PROCESSING';
          return (
            <ContentCard
              key={document.id}
              data-document-id={document.id}
              title={document.title || document.source}
              badge={<DocumentStatusIcon status={document.status} />}
              meta={
                document.status === 'FAILED' && document.error_message ? (
                  <>
                    <span className='text-red-500'>
                      {document.error_message}
                    </span>
                    {' · '}
                    {formatKnowledgeDate(document.created_at)}
                  </>
                ) : (
                  formatKnowledgeDate(document.created_at)
                )
              }
              menu={[
                {
                  label: 'Reindex',
                  icon: <Refresh size={16} />,
                  disabled: indexing || reindex.isPending,
                  onSelect: () => reindex.mutate(document.id),
                },
                {
                  label: 'Delete',
                  icon: (
                    <Trash
                      size={16}
                      className='text-red-500'
                    />
                  ),
                  destructive: true,
                  onSelect: () => setDeleting(document),
                },
              ]}
            />
          );
        }}
      />

      <AddFilesDialog
        kbaseId={kbaseId}
        open={adding}
        onOpenChange={setAdding}
      />
      <ConfirmationDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        cancelHandler={() => setDeleting(null)}
        confirmationHandler={() => {
          if (!deleting) return;
          remove.mutate(
            { id: deleting.id },
            { onSuccess: () => setDeleting(null) }
          );
        }}
      />
    </>
  );
}
